'use client';

import { replaceTokensWithComponents } from '@cat-ecom/common/utils/stringUtils';
import { PurifiedHtml } from 'components/purified-html';
import HeightLimitedExpander from 'components/height-limited-expander';
import { CatHeading } from 'components/blocks-components';
import Prop65Warning from './prop65-warning';
import HoseWarning from './hose-warning';
import AnchorMenuMobileSectionHeader from '../anchor-menu-mobile/anchor-menu-mobile-section-header/anchor-menu-mobile-section-header';
import { useTranslations } from 'next-intl';
import { ProductDetail } from 'types/product';
import { Conditional } from '@cat-ecom/pcc-components';
import { useRef, useEffect, useState } from 'react';
import cx from 'classnames';
import styles from './anchor-menu-description.module.scss';

export interface AnchorMenuDescriptionProps {
  data: ProductDetail;
  isMobile?: boolean;
  isPCC_PDP_headerUpdateMobileView?: boolean;
}
const AnchorMenuDescription = ({
  data,
  isPCC_PDP_headerUpdateMobileView = false,
  isMobile = false,
}) => {
  const t = useTranslations();
  const desktopTitle = replaceTokensWithComponents(
    t('PDP_DESC_FOR_PART', { '0': '{0}' }),
    data.partNumber
  );
  const textRef = useRef(null);
  const [isEllipsis, setIsEllipsis] = useState(false);

  const { prop65 = '', hoseWarning = '', longDescription = '' } = data;
  const isDescriptionAvailable = longDescription || prop65;
  useEffect(() => {
    const el = textRef.current;
    if (el && isMobile && longDescription && isPCC_PDP_headerUpdateMobileView) {
      const checkEllipsis = () => {
        setIsEllipsis(el.scrollHeight > el.clientHeight);
      };
      const resizeObserver = new ResizeObserver(checkEllipsis);
      resizeObserver.observe(el);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [isMobile, longDescription, isPCC_PDP_headerUpdateMobileView]);
  return (
    <>
      <Conditional
        test={isMobile && isPCC_PDP_headerUpdateMobileView}
        fallback={<CatHeading variant="title" className='cat-u-theme-typography-title'>
          {desktopTitle}</CatHeading>}
      >
        <div className="mb-3">
          <AnchorMenuMobileSectionHeader
            titleText={t('ORG_DESCRIPTION')}
            shouldShowButton={isEllipsis && isDescriptionAvailable}
          >
            {
              <div className="cat-u-margin-top-md">
                <PurifiedHtml
                  as="p"
                  htmlString={data?.longDescription}
                  className="mb-4"
                />
                {prop65 && (
                  <Prop65Warning
                    prop65={prop65}
                    isMobile={isMobile}
                    isPCC_PDP_headerUpdateMobileView={
                      isPCC_PDP_headerUpdateMobileView
                    }
                  />
                )}
                {hoseWarning && (
                  <HoseWarning hoseWarning={hoseWarning} viewBtnShwn={false} />
                )}
              </div>
            }
          </AnchorMenuMobileSectionHeader>
        </div>
      </Conditional>

      <Conditional
        test={isDescriptionAvailable}
        fallback={
          <p className="cat-u-margin-bottom-none cat-u-theme-typography-body mt-3">
            {t('PART_DESCRIPTION_INFORMATION_NOT_AVAILABLE')}
          </p>
        }
      >
        <Conditional
          test={isPCC_PDP_headerUpdateMobileView && isMobile}
          fallback={
            <div className="cat-u-margin-top-md">
              {data?.longDescription && (
                <HeightLimitedExpander
                  buttonSize="md"
                  id="descriptionExpander"
                  className="cat-u-theme-typography-body mb-2"
                  lessButtonText={t('VIEW_LESS')}
                  moreButtonText={t('VIEW_MORE_PRODUCTS')}
                >
                  <PurifiedHtml
                    as="p"
                    htmlString={data?.longDescription}
                    className="cat-u-margin-bottom-none"
                  />
                </HeightLimitedExpander>
              )}
              {prop65 && <Prop65Warning prop65={prop65} />}
              {hoseWarning && <HoseWarning hoseWarning={hoseWarning} />}
            </div>
          }
        >
          <>
            {data?.longDescription && (
              <div
                ref={textRef}
                className={cx('cat-u-margin-top-sm', 'mb-3', {
                  [styles['multiline-ellipsis4']]: hoseWarning || prop65,
                  [styles['multiline-ellipsis6']]: !hoseWarning && !prop65
                })}
              >
                <PurifiedHtml
                  as="p"
                  htmlString={data?.longDescription}
                  className="mb-2"
                />
              </div>
            )}
            {prop65 && <Prop65Warning prop65={prop65} isMobile={isMobile} />}
            {hoseWarning && <HoseWarning hoseWarning={hoseWarning} />}
          </>
        </Conditional>
      </Conditional>
    </>
  );
};

export default AnchorMenuDescription;
